import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import ReorderResizeTable from 'components/ReorderResizeTable';
import { useNavigate } from 'react-router-dom';
import { getDataRoomRoute } from 'utils';
import AnalyticsCard from 'router/subrouters/Dash/subrouters/private/subrouters/Enterprise/pages/Analytics/components/AnalyticsCard';
import { useTranslation } from 'react-i18next';
import {
  HEADING_TRANSLATIONS,
  SIZE_VALUES,
  SORTING_VALUES,
  TABLE_COLUMNS,
  LIMIT,
} from './components/data';
import { BODY_CONTENT_GENERATE } from './components';
import { DataRoom } from 'router/subrouters/DataRoom/models/DataRoom';
import useQueryParams from 'hooks/useQueryParams';
import { Button, Loader } from 'ncoded-component-library';
import PlusIcon from 'icons/Plus.icon';
import useUndoActions from 'hooks/useUndoActions';
import CreateDataRoomContext from 'providers/CreateDataRoom/CreateDataRoom.context';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import PopNotificationWithUndo from 'components/PopNotificationWithUndo';
import InfiniteScroll from 'components/InfiniteScroll';
import useInfinitePagination from 'hooks/useInfinitePagination';
import { Enterprise } from 'models/Enterprises';
import api from 'api';
import { SIZE } from 'models/Size';
import ReportsEmptyState from 'router/subrouters/DataRoom/subrouters/Reports/pages/Reports/components/ReportsEmptyState';
import useCanAccessDataRoom from 'hooks/useCanAccessDataRoom';
import { TABLES_WITH_COLUMN_SETTINGS } from 'models/User';
import useTableColumnSettings from 'components/ReorderResizeTable/hooks/useTableColumnSettings';

import './EnterpriseDataRooms.styles.scss';

type EnterpriseDataRoomsProps = {
  className?: string;
  refetchFlag?: boolean;
  enterprise: Partial<Enterprise>;
};

const EnterpriseDataRooms: React.FC<EnterpriseDataRoomsProps> = (props) => {
  const { className, enterprise, refetchFlag } = props;

  const classes = classNames('enterprise-data-rooms', 'white-panel', className);

  const [selectedDataRoom, setSelectedDataRoom] = useState('');

  const { isSuperAdmin } = useContext(CurrentUserContext);
  const { openCreateDataRoomModal } = useContext(CreateDataRoomContext);

  const getCanAccessDataRoom = useCanAccessDataRoom();

  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    params: { dataRoomSort },
    setQueryParam,
  } = useQueryParams();

  const { isUndoAvailable, setUndoTimeout, clearUndoTimeout } =
    useUndoActions();

  const { columns, columnSizing, onReorder, onColumnSizingChange } =
    useTableColumnSettings(
      TABLES_WITH_COLUMN_SETTINGS.ENTERPRISE_DATA_ROOMS,
      TABLE_COLUMNS,
    );

  const { loading, items, setItems, onContainerScrolled } =
    useInfinitePagination<DataRoom>({
      condition: !!enterprise?._id,
      resetDeps: [enterprise?._id, dataRoomSort, refetchFlag],
      dependencies: [enterprise?._id, dataRoomSort, refetchFlag],
      makeRequest: async (currentPage) =>
        api.enterpriseAnalytics
          .getAllDataRooms(enterprise?._id, {
            $page: currentPage.toString(),
            $limit: LIMIT,
            $sort: dataRoomSort,
          })
          .then(({ data }) => data),
    });

  const itemsLength = useMemo(() => items.length, [items.length]);

  const updateItem = useCallback(
    (dataRoom: Partial<DataRoom>) =>
      setItems((old) =>
        old.map((el) =>
          el._id === dataRoom._id ? { ...el, ...dataRoom } : el,
        ),
      ),
    [setItems],
  );

  const onUndo = useCallback(() => {
    try {
      api.dataRoom.access.undoRequestAccess(selectedDataRoom);
      updateItem({ _id: selectedDataRoom, requestedDataRoomAccess: false });
      clearUndoTimeout();
    } catch {
      return;
    }
  }, [selectedDataRoom, updateItem, clearUndoTimeout]);

  const onGrantAccess = useCallback(
    (dataRoomId: string) => {
      updateItem({ _id: dataRoomId, requestedDataRoomAccess: true });
      setSelectedDataRoom(dataRoomId);
      setUndoTimeout();
    },
    [setUndoTimeout, updateItem],
  );

  const renderedContent = useMemo(
    () =>
      TABLE_COLUMNS.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (dataRoom: DataRoom) =>
            BODY_CONTENT_GENERATE[col]({ dataRoom, onGrantAccess }),
        }),
        {},
      ),
    [onGrantAccess],
  );

  return (
    <AnalyticsCard
      className={classes}
      title={t('dataRooms')}
      {...(!isSuperAdmin && {
        action: (
          <Button
            variant="outline"
            icon={PlusIcon}
            onClick={openCreateDataRoomModal}
          >
            {t('newDataRoom')}
          </Button>
        ),
      })}
    >
      {loading && !!itemsLength && <Loader overlay={false} />}

      {!loading && !itemsLength ? (
        <ReportsEmptyState size={SIZE.BIG} />
      ) : (
        <div style={{ position: 'relative' }}>
          <InfiniteScroll
            role="listbox"
            localScroll
            className="enterprise-data-rooms__scroll"
            onScroll={onContainerScrolled}
          >
            <ReorderResizeTable
              sort={dataRoomSort}
              columns={columns}
              data={items}
              isDataLoading={loading && !itemsLength}
              sortingValues={SORTING_VALUES}
              customSizes={SIZE_VALUES}
              headingTranslations={HEADING_TRANSLATIONS}
              tableContentTransformations={renderedContent}
              updateSort={(param) => setQueryParam('dataRoomSort', param)}
              columnSizing={columnSizing}
              onColumnSizingChange={onColumnSizingChange}
              onReorder={onReorder as (columns: Array<keyof DataRoom>) => void}
              onRowClick={({ _id, hasDataRoomAccess, dealRoom }) => {
                if (getCanAccessDataRoom(dealRoom, hasDataRoomAccess))
                  navigate(getDataRoomRoute(_id));
              }}
            />
          </InfiniteScroll>
        </div>
      )}

      <PopNotificationWithUndo
        isUndoAvailable={isUndoAvailable}
        disabled={loading}
        onUndo={onUndo}
      >
        <p>{t('weEmailedOwnerAboutYourRequest')}</p>
      </PopNotificationWithUndo>
    </AnalyticsCard>
  );
};

export default EnterpriseDataRooms;
